'use client'
import React, { JSX, useLayoutEffect, useMemo, useRef } from 'react';
import classNames from "classnames";

import { Map } from "@/migrated-widgets/table-map/components/molecules/Map";
import ShapeMarker from "@/migrated-widgets/table-map/components/molecules/Map/ShapeMarker";
import { SpinnerBox } from "@/migrated-widgets/table-map/components/atoms/Spinner/SpinnerBox";
import { useTableMapState } from "./TableMapState";

import { randomNumber } from "@/migrated-widgets/table-map/utils/random";
import { MapSideProps } from "./types";
import TagCircleMarker from '@/migrated-widgets/table-map/components/molecules/Map/TagCircleMarker';
import FilterCircle from '@/migrated-widgets/table-map/components/molecules/Map/FilterCircle';
import { productColors, locationProducts } from './constants';
import { DealerPinIcon } from "@/migrated-widgets/table-map/components/icons/DealerPinIcon"
import { CompetitorPinIcon } from '@/migrated-widgets/table-map/components/icons/CompetitorPinIcon';
import { Environments } from '@/migrated-widgets/table-map/constants/Environments';
import { Feature, MapContextType } from "@/migrated-widgets/table-map/components/molecules/Map/types";
import { cloneDeep } from 'lodash';
import chroma from "chroma-js";
import { PenToolIcon } from 'lucide-react';
import { Tooltip } from '@/components/molecules/Tooltip';
import { CloseIcon } from '../../icons/CloseIcon';


const MapSide: React.FC<MapSideProps> = () => {
    const {
        zipCodes,
        setZipCodes,
        circleMarkers,
        geometryFeatures,
        salesMarkers,
        repairOrdersMarkers,
        leadsMarkers,
        zipCodeNewMarkers,
        zipCodeUsedMarkers,
        loadingLocations,
        loadingTransactions,
        locationsBy,
        campaignColors,
        dealerMarkers,
        loadingDealers,
        zoomResetTrigger,
        showingTransactionType,
        competitorsMarkers,
        loadingCompetitors,
        tableView,
        filterCircle,
        setFilterCircle,
        setMapZoom,
    } = useTableMapState();

    const mapProvider = useRef<MapContextType>(null);
    const circleFilterTooltipContainer = useRef<HTMLDivElement>(null);

    const handleSelectZipCode = (zipCode: string | undefined) => {
        if (!zipCode) return;
        if (tableView === 'byModels') {
            setZipCodes([zipCode]);
        } else {
            setZipCodes((prev) =>
                prev.includes(zipCode) ? prev.filter((z) => z !== zipCode) : [...prev, zipCode]
            );
        }
    }

    const handleToggleFilterCircle = () => {
        if (filterCircle) {
            setFilterCircle(null);
            return;
        }
        const mapInstance = mapProvider.current?.map;
        const mapCenter = mapInstance?.getCenter();
        setFilterCircle({
            center: mapCenter
                ? { lat: mapCenter.lat(), lng: mapCenter.lng() }
                : { lat: dealerMarkers[0]?.position.lat ?? 33.749, lng: dealerMarkers[0]?.position.lng ?? -84.388 },
            radius: 50000,
        });
    };



    const circleMarkersRender = useMemo(() => {
        const markers: JSX.Element[] = [];
        circleMarkers.sort((a, b) => b.radius - a.radius).forEach((marker, index) => {
            for (const location of marker.locations) {
                const color = locationsBy === 'product' ? productColors[location.product] ?? '' : campaignColors[location.campaignName];
                if (locationsBy === 'product') {
                    const productName = locationProducts.find((locationProduct) => locationProduct.value === location.product)?.name;
                    if (productName) {
                        markers.push(<ShapeMarker
                            label={`<strong>${locationsBy.charAt(0).toUpperCase() + locationsBy.slice(1)} :</strong> ${productName}<br>${marker.properties.label}`}
                            strokeColor={chroma.valid(color) ? chroma(color).darken().hex() : 'gray'}
                            strokeOpacity={0.625}
                            strokeWeight={1.25}
                            fillColor={chroma.valid(color) ? color : 'gray'}
                            fillOpacity={0.2}
                            zIndex={2}
                            radius={marker.radius}
                            shape="circle"
                            key={`location_${index}_${randomNumber()}`}
                            center={marker.position}
                        />)
                        break;
                    }
                    continue;
                }

                markers.push(<ShapeMarker
                    label={`<strong>${locationsBy.charAt(0).toUpperCase() + locationsBy.slice(1)} :</strong> ${location.campaignName}<br>${marker.properties.label}`}
                    strokeColor={chroma.valid(color) ? chroma(color).darken().hex() : 'gray'}
                    strokeOpacity={0.625}
                    strokeWeight={1.25}
                    fillColor={chroma.valid(color) ? color : 'gray'}
                    fillOpacity={0.2}
                    zIndex={2}
                    radius={marker.radius}
                    shape="circle"
                    key={`location_${index}_${randomNumber()}`}
                    center={marker.position}
                />)
            }
        })
        return markers;
    }, [circleMarkers, campaignColors, productColors])

    const geometryMarkers = useMemo(() => {
        console.log('### CAMPAIGN COLORS', campaignColors)
        const features: Feature[] = [];
        geometryFeatures.forEach((feature) => {
            for (const location of feature.locations) {
                const color = locationsBy === 'product' ? productColors[location.product] ?? '' : campaignColors[location.campaignName];
                const feat = cloneDeep(feature);
                feat['properties']['label'] = `<strong>${locationsBy.charAt(0).toUpperCase() + locationsBy.slice(1)} :</strong> ${locationsBy === 'product' ? locationProducts.find((locationProduct) => locationProduct.value === location.product)?.name ?? '' : location.campaignName}<br>${feat['properties']['label']}`;
                feat['properties']['strokeColor'] = chroma.valid(color) ? chroma(color).darken().hex() : 'gray';
                feat['properties']['strokeOpacity'] = 0.625;
                feat['properties']['strokeWeight'] = 1.25;
                feat['properties']['fillColor'] = chroma.valid(color) ? color : 'gray';
                feat['properties']['fillOpacity'] = 0.2;
                feat['properties']['zIndex'] = 4;
                features.push(feat);
                if (locationsBy === 'product') {
                    break;
                }
            }
        })
        return features;
    }, [geometryFeatures, locationsBy, campaignColors, productColors])

    useLayoutEffect(() => {
        if (dealerMarkers.length && mapProvider.current) {
            const [dealerMarker] = dealerMarkers;
            mapProvider.current.centerBy([dealerMarker.position], 9);
        }
    }, [dealerMarkers, mapProvider.current?.map, zoomResetTrigger])

    useLayoutEffect(() => {
        const map = mapProvider.current?.map;
        if (!map) return;
        setMapZoom(map.getZoom() ?? 9);
        const listener = map.addListener('zoom_changed', () => {
            setMapZoom(map.getZoom() ?? 9);
        });
        return () => google.maps.event.removeListener(listener);
    }, [mapProvider.current?.map])

    const stopDragPropagation = (e: React.PointerEvent | React.MouseEvent | React.TouchEvent) => {
        e.stopPropagation();
    };


    const salesOrRepairMarkers = useMemo(() => {
        // Zip code views: use markers computed from the same data as the table
        if (tableView === 'zipCodesNew') {
            let markers = cloneDeep(zipCodeNewMarkers);
            if (zipCodes.length > 0) {
                markers = markers.filter((m) => m.zipCode && zipCodes.includes(m.zipCode));
            }
            return markers.filter((m) => m.totalSold > 0);
        }
        if (tableView === 'zipCodesUsed') {
            let markers = cloneDeep(zipCodeUsedMarkers);
            if (zipCodes.length > 0) {
                markers = markers.filter((m) => m.zipCode && zipCodes.includes(m.zipCode));
            }
            return markers.filter((m) => m.totalSold > 0);
        }
        // byModels view: original drill-down logic
        let markers = cloneDeep(
            showingTransactionType === 'S' ? salesMarkers :
                showingTransactionType === 'R' ? repairOrdersMarkers : []
        ) ?? [];
        if (zipCodes.length > 0) {
            markers = markers.filter((m) => m.zipCode && zipCodes.includes(m.zipCode));
        }
        return markers;
    }, [salesMarkers, repairOrdersMarkers, zipCodeNewMarkers, zipCodeUsedMarkers, showingTransactionType, tableView, zipCodes])

    const filteredLeadsMarkers = useMemo(() => {
        let markers = cloneDeep(leadsMarkers);

        if (zipCodes.length > 0) {
            markers = markers.filter((m) => m.zipCode && zipCodes.includes(m.zipCode));
        }

        if (tableView === 'zipCodesNew') {
            markers = markers
                .map((m) => ({ ...m, totalSold: m.newSold }))
                .filter((m) => m.totalSold > 0);
        } else if (tableView === 'zipCodesUsed') {
            markers = markers
                .map((m) => ({ ...m, totalSold: m.usedSold }))
                .filter((m) => m.totalSold > 0);
        }

        return markers;
    }, [leadsMarkers, tableView, zipCodes])

    return (
        <div
            ref={circleFilterTooltipContainer}
            // Clip the map to its 529px box so Google's pre-rendered off-viewport
            // tiles (un-clipped during PDF export) can't bleed onto the next PDF page. This
            // class survives the export overflow overrides in useExportPageAsPdf (they only
            // target inline-overflow and col-span-* elements), unlike the grid cell wrapper.
            className="w-full h-full relative overflow-hidden"
            onPointerDown={stopDragPropagation}
            onMouseDown={stopDragPropagation}
            onTouchStart={stopDragPropagation}
        >
            {(loadingLocations || loadingTransactions || loadingDealers || loadingCompetitors) &&
                <SpinnerBox boxBg="white" color="primary" size={5} boxClassName="bg-opacity-80" />
            }

            <div className="absolute right-[10px] bottom-[80px] z-10">
                <Tooltip disablePortal position="left" content={filterCircle ? 'Remove area' : 'Draw area'}>
                    <button
                        onClick={handleToggleFilterCircle}
                        title={filterCircle ? 'Remove area filter' : 'Draw area filter'}
                        className={classNames(
                            "w-10 h-10 flex items-center justify-center select-none transition-all",
                            "shadow-[0_1px_4px_rgba(0,0,0,0.3)]",
                            "cursor-pointer duration-300 transition-all ease-in-out rounded-full hover:scale-105 hover:translate-y-[-2px] ",
                            filterCircle
                                ? "bg-table-map-black text-white"
                                : "bg-white text-table-map-black"
                        )}
                    >
                        {filterCircle ? (
                            <CloseIcon width={28} />
                        ) : (
                            <PenToolIcon width={18} />
                        )}
                    </button>
                </Tooltip>
            </div>

            <Map
                center={{ lat: 2.8, lng: -187.3 }}
                mapTypeId="terrain"
                mapTypeControl={false}
                streetViewControl={false}
                mapId={Environments.TABLE_MAP_WIDGET_MAP_ID}
                data={geometryMarkers}
                maxZoom={16}
                zoom={9}
                centerByTags={false}
                ref={mapProvider}
            >

                {circleMarkersRender}


                {
                    salesOrRepairMarkers.map((marker, index) => (
                        <TagCircleMarker
                            key={`zipcode_${JSON.stringify(marker)}_${index}`}
                            label={`<div style="text-align:center;">Zip Code<br><strong>${marker.zipCode}</strong></div>`}
                            onClick={() => handleSelectZipCode(marker.zipCode)}
                            zIndex={8}
                            position={marker.position}>
                            <div className={classNames(
                                "z-999 rounded-full flex items-center justify-center marker-zoomIn-animation shadow-[0_4px_6px_-4px_rgba(0,0,0,0.1),_0_10px_15px_-3px_rgba(0,0,0,0.1)] bg-table-map-yellow",
                                { 'w-6 h-6': marker.totalSold <= 1 },
                                { 'w-7 h-7': marker.totalSold > 1 && marker.totalSold <= 5 },
                                { 'w-8 h-8': marker.totalSold > 5 && marker.totalSold <= 15 },
                                { 'w-9 h-9': marker.totalSold > 15 && marker.totalSold <= 30 },
                                { 'w-10 h-10': marker.totalSold > 30 && marker.totalSold <= 50 },
                                { 'w-11 h-11': marker.totalSold > 50 && marker.totalSold <= 75 },
                                { 'w-12 h-12': marker.totalSold > 75 && marker.totalSold <= 100 },
                                { 'w-13 h-13': marker.totalSold > 100 },
                            )}>
                                <span className="text-xs font-bold text-table-map-black">
                                    {marker.totalSold}
                                </span>
                            </div>
                        </TagCircleMarker>
                    ))
                }


                {
                    filteredLeadsMarkers.map((marker, index) => (
                        <TagCircleMarker
                            key={`leads_${marker.zipCode}_${index}`}
                            label={`<div style="text-align:center;">Zip Code<br><strong>${marker.zipCode}</strong></div>`}
                            onClick={() => handleSelectZipCode(marker.zipCode)}
                            zIndex={8}
                            position={marker.position}>
                            <div className={classNames(
                                "z-999 rounded-full text-white flex items-center justify-center marker-zoomIn-animation shadow-[0_4px_6px_-4px_rgba(0,0,0,0.1),_0_10px_15px_-3px_rgba(0,0,0,0.1)]",
                                zipCodes.includes(marker.zipCode || '') ? 'bg-secondary' : 'bg-secondary',
                                { 'w-6 h-6': marker.totalSold <= 1 },
                                { 'w-7 h-7': marker.totalSold > 1 && marker.totalSold <= 5 },
                                { 'w-8 h-8': marker.totalSold > 5 && marker.totalSold <= 15 },
                                { 'w-9 h-9': marker.totalSold > 15 && marker.totalSold <= 30 },
                                { 'w-10 h-10': marker.totalSold > 30 && marker.totalSold <= 50 },
                                { 'w-11 h-11': marker.totalSold > 50 && marker.totalSold <= 75 },
                                { 'w-12 h-12': marker.totalSold > 75 && marker.totalSold <= 100 },
                                { 'w-13 h-13': marker.totalSold > 100 },
                            )}>
                                <span className={classNames("text-xs font-bold", 'text-white')}>
                                    {marker.totalSold}
                                </span>
                            </div>
                        </TagCircleMarker>
                    ))
                }

                {
                    dealerMarkers.map((marker) => (
                        <TagCircleMarker
                            key={`dealer_${marker.id}_${randomNumber()}`}
                            label={`<strong>${marker.label}</strong> `}
                            zIndex={10}
                            position={{
                                lat: marker.position.lat,
                                lng: marker.position.lng
                            }}>
                            <DealerPinIcon height="52" />
                        </TagCircleMarker>
                    ))
                }

                {
                    competitorsMarkers.map((marker) => (
                        <TagCircleMarker
                            key={`competitor_${marker.id}_${randomNumber()}`}
                            label={`<strong>${marker.label}</strong> `}
                            zIndex={9}
                            position={{
                                lat: marker.position.lat,
                                lng: marker.position.lng
                            }}>
                            <CompetitorPinIcon />
                        </TagCircleMarker>
                    ))
                }

                {filterCircle && (
                    <FilterCircle
                        center={filterCircle.center}
                        radius={filterCircle.radius}
                        onCenterChange={(center) => setFilterCircle((prev) => prev ? { ...prev, center } : null)}
                        onRadiusChange={(radius) => setFilterCircle((prev) => prev ? { ...prev, radius } : null)}
                    />
                )}

            </Map>
        </div>
    )
}
export default MapSide